import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { getStoredEmojified, useEmoji } from '../context/EmojiContext';
import { listEmails } from '../services/emailCredentialService';
import { createDispatchJob, verifyRecipientCsv } from '../services/emailService';
import '../components/DataPage.css';
import './SendEmail.css';
import { AddIcon, EmptyDocIcon, SendIcon } from '../components/Icons';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS_TOTAL_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;
const SEND_LIMIT = 500;
const WINDOW_HOURS = 24;

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const senderTableStyles = {
  table: {
    style: {
      backgroundColor: 'transparent',
    },
  },
  headRow: {
    style: {
      backgroundColor: 'var(--color-surface)',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
  },
  headCells: {
    style: {
      color: 'var(--color-text-muted)',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  rows: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'var(--color-accent-soft)',
    },
  },
  cells: {
    style: {
      color: 'var(--color-text)',
      fontSize: '0.88rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  noData: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
    },
  },
};

function splitCsvLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      cells.push(current.trim().replace(/^"|"$/g, ''));
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current.trim().replace(/^"|"$/g, ''));
  return cells;
}

function isEmptyCsvRow(cells) {
  return cells.every((cell) => !String(cell ?? '').trim());
}

async function validateCsvFile(file) {
  if (!file) {
    return 'Please upload a CSV file.';
  }

  if (!file.name.toLowerCase().endsWith('.csv')) {
    return 'Only .csv files are supported.';
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'File is too large. Maximum size is 10MB.';
  }

  const text = await file.text();
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== '');

  if (!lines.length) {
    return 'CSV file has no data rows.';
  }

  const rawHeaders = splitCsvLine(lines[0]);
  // Drop empty header columns
  const headerEntries = rawHeaders
    .map((header, index) => ({ header: header.trim(), index }))
    .filter((entry) => entry.header !== '');

  if (!headerEntries.length) {
    return 'CSV must include columns: name, port';
  }

  const headersLower = headerEntries.map((entry) => entry.header.toLowerCase());
  const hasName = headersLower.includes('name');
  const hasPort = headersLower.includes('port');

  if (!hasName || !hasPort) {
    return 'CSV must include columns: name, port';
  }

  const nameIndex = headerEntries.find((entry) => entry.header.toLowerCase() === 'name').index;
  const portIndex = headerEntries.find((entry) => entry.header.toLowerCase() === 'port').index;

  const dataRows = lines.slice(1).filter((line) => {
    const cells = splitCsvLine(line);
    if (isEmptyCsvRow(cells)) {
      return false;
    }

    const name = String(cells[nameIndex] ?? '').trim();
    const port = String(cells[portIndex] ?? '').trim();
    // Keep rows that have at least name or port; backend still requires name
    return name !== '' || port !== '';
  });

  if (!dataRows.length) {
    return 'CSV file has no data rows after removing empty rows.';
  }

  const rowsWithName = dataRows.filter((line) => {
    const cells = splitCsvLine(line);
    return String(cells[nameIndex] ?? '').trim() !== '';
  });

  if (!rowsWithName.length) {
    return 'CSV has no rows with a vessel name after removing empty rows.';
  }

  return null;
}

function getAvailableQuota(entry) {
  return entry.available ?? Math.max(0, SEND_LIMIT - (entry.used ?? 0));
}

function getSentInWindow(entry) {
  return entry.sentInWindow ?? 0;
}

function getRemainingQuota(entry) {
  return getAvailableQuota(entry);
}

function hasAnyQuota(entry) {
  return getAvailableQuota(entry) > 0;
}

function getTotalAvailableQuota(emails = []) {
  return emails.reduce((sum, entry) => sum + getAvailableQuota(entry), 0);
}

/** Greedy plan: most available first. Returns [] if not enough capacity. */
function buildSenderAllocation(emails, requiredSends = 1) {
  if (!requiredSends || requiredSends < 1 || !emails?.length) {
    return [];
  }

  const ranked = [...emails]
    .filter((entry) => hasAnyQuota(entry))
    .sort((left, right) => {
      const diff = getAvailableQuota(right) - getAvailableQuota(left);
      if (diff !== 0) {
        return diff;
      }
      return left.id - right.id;
    });

  if (getTotalAvailableQuota(ranked) < requiredSends) {
    return [];
  }

  let remaining = requiredSends;
  const allocations = [];

  for (const entry of ranked) {
    if (remaining <= 0) {
      break;
    }
    const available = getAvailableQuota(entry);
    if (available <= 0) {
      continue;
    }
    const count = Math.min(available, remaining);
    allocations.push({
      senderId: entry.id,
      email: entry.email,
      count,
      available,
    });
    remaining -= count;
  }

  return remaining > 0 ? [] : allocations;
}

function formatAllocationSummary(allocations) {
  if (!allocations?.length) {
    return '';
  }

  return allocations
    .map((entry) => `${entry.count.toLocaleString()} from ${entry.email}`)
    .join(' · ');
}

function formatDispatchDateTime(value) {
  if (!value) {
    return '';
  }

  return new Date(value).toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function showDispatchToast(result) {
  const isScheduled = result.scheduled && result.sendAt;
  const prefix = getStoredEmojified() ? '✅ ' : '';
  const title = isScheduled ? `${prefix}Scheduled` : `${prefix}Sent`;
  const allocationText = Array.isArray(result.senderAllocations) && result.senderAllocations.length
    ? result.senderAllocations
        .map((entry) => `${Number(entry.count).toLocaleString()} from ${entry.senderEmail}`)
        .join(' · ')
    : result.senderEmail;
  const detail = isScheduled
    ? `${result.queued.toLocaleString()} email(s) for ${formatDispatchDateTime(result.sendAt)} — ${allocationText}`
    : `${result.queued.toLocaleString()} email(s) queued — ${allocationText}`;

  toast.success(
    <div className="dispatch-toast">
      <strong>{title}</strong>
      <span>{detail}</span>
      <code>{result.batchId}</code>
    </div>
  );
}

function SendEmail() {
  const { isEmojified, emojify } = useEmoji();
  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [sendAt, setSendAt] = useState('');
  const [scheduleMode, setScheduleMode] = useState('now');
  const [senderEmails, setSenderEmails] = useState([]);
  const [senderMode, setSenderMode] = useState('auto');
  const [selectedSenderIds, setSelectedSenderIds] = useState([]);
  const [senderSearch, setSenderSearch] = useState('');
  const [senderError, setSenderError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const isDisabled = isValidating || isDispatching;

  const resetValidation = () => {
    setValidationResult(null);
    setSenderError('');
    setSelectedSenderIds([]);
  };

  const resetForm = useCallback(() => {
    setCsvFile(null);
    setSubject('');
    setBody('');
    setAttachments([]);
    setAttachmentError('');
    setSendAt('');
    setScheduleMode('now');
    setSenderMode('auto');
    setSenderError('');
    setSenderSearch('');
    setSelectedSenderIds([]);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  }, []);

  const recipientCount = validationResult?.matched?.length ?? 0;
  const isRecipientsReady = recipientCount > 0;

  const isScheduleStepComplete = useMemo(() => {
    if (!isRecipientsReady) {
      return false;
    }
    if (scheduleMode === 'now') {
      return true;
    }
    if (!sendAt) {
      return false;
    }
    const scheduledDate = new Date(sendAt);
    return !Number.isNaN(scheduledDate.getTime()) && scheduledDate.getTime() > Date.now();
  }, [isRecipientsReady, scheduleMode, sendAt]);

  const quotaAsOf = useMemo(() => {
    if (scheduleMode !== 'later' || !sendAt) {
      return null;
    }
    const scheduledDate = new Date(sendAt);
    if (Number.isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= Date.now()) {
      return null;
    }
    return scheduledDate.toISOString();
  }, [scheduleMode, sendAt]);

  const autoAllocation = useMemo(
    () =>
      isRecipientsReady && isScheduleStepComplete
        ? buildSenderAllocation(senderEmails, recipientCount)
        : [],
    [isRecipientsReady, isScheduleStepComplete, senderEmails, recipientCount]
  );

  const selectedSenders = useMemo(
    () => senderEmails.filter((entry) => selectedSenderIds.includes(entry.id)),
    [senderEmails, selectedSenderIds]
  );

  const manualAllocation = useMemo(
    () =>
      isRecipientsReady && isScheduleStepComplete && selectedSenders.length
        ? buildSenderAllocation(selectedSenders, recipientCount)
        : [],
    [isRecipientsReady, isScheduleStepComplete, selectedSenders, recipientCount]
  );

  const activeAllocation = senderMode === 'auto' ? autoAllocation : manualAllocation;
  const selectedAvailableQuota = useMemo(
    () => getTotalAvailableQuota(selectedSenders),
    [selectedSenders]
  );

  const isSenderStepComplete =
    isScheduleStepComplete &&
    (senderMode === 'auto'
      ? autoAllocation.length > 0
      : selectedSenderIds.length > 0 && manualAllocation.length > 0);
  const isMessageStepReady = isSenderStepComplete;
  const isMessageStepComplete =
    isMessageStepReady && Boolean(subject.trim()) && Boolean(body.trim());
  const isSummaryStepReady = isMessageStepComplete;

  const totalAvailableQuota = useMemo(
    () => getTotalAvailableQuota(senderEmails),
    [senderEmails]
  );

  const quotaWindowLabel = quotaAsOf
    ? `around ${formatDispatchDateTime(quotaAsOf)}`
    : `last ${WINDOW_HOURS} hours`;

  const senderTableColumns = useMemo(
    () => [
      {
        name: 'Email',
        selector: (row) => row.email,
        sortable: true,
        grow: 2,
        cell: (row) => <span className="data-page__cell-truncate">{row.email}</span>,
      },
      {
        name: 'Provider',
        selector: (row) => row.provider || '',
        sortable: true,
        grow: 1,
        cell: (row) => row.provider || '—',
      },
      {
        name: `Sent (${WINDOW_HOURS}h)`,
        selector: (row) => getSentInWindow(row),
        sortable: true,
        right: true,
        cell: (row) => getSentInWindow(row).toLocaleString(),
      },
      {
        name: 'Sends left',
        selector: (row) => getRemainingQuota(row),
        sortable: true,
        right: true,
        cell: (row) => getRemainingQuota(row).toLocaleString(),
      },
    ],
    []
  );

  const filteredSenderEmails = useMemo(() => {
    const query = senderSearch.trim().toLowerCase();
    if (!query) {
      return senderEmails;
    }

    const tokens = query.split(/\s+/).filter(Boolean);
    return senderEmails.filter((entry) => {
      const haystack = [entry.email, entry.display_name, entry.displayName, entry.provider]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [senderEmails, senderSearch]);

  const isSenderRowSelected = useCallback(
    (row) => selectedSenderIds.includes(row.id),
    [selectedSenderIds]
  );

  const handleSenderRowsSelected = useCallback(
    ({ selectedRows }) => {
      const visibleIds = new Set(filteredSenderEmails.map((row) => row.id));
      setSelectedSenderIds((current) => {
        const next = [
          ...current.filter((id) => !visibleIds.has(id)),
          ...selectedRows.map((row) => row.id),
        ];
        if (next.length === current.length && next.every((id) => current.includes(id))) {
          return current;
        }
        return next;
      });
      setSenderError('');
    },
    [filteredSenderEmails]
  );

  useEffect(() => {
    setSelectedSenderIds((current) => {
      if (!current.length) {
        return current;
      }

      const stillValid = current.filter((id) => {
        const entry = senderEmails.find((row) => row.id === id);
        return entry && hasAnyQuota(entry);
      });

      if (
        stillValid.length === current.length &&
        stillValid.every((id, index) => id === current[index])
      ) {
        return current;
      }

      return stillValid;
    });
  }, [recipientCount, senderEmails]);

  useEffect(() => {
    setSelectedSenderIds([]);
    setSenderError('');
  }, [quotaAsOf, scheduleMode]);

  useEffect(() => {
    let isMounted = true;

    async function loadSenderEmails() {
      try {
        const emails = await listEmails(quotaAsOf ? { asOf: quotaAsOf } : {});
        if (isMounted) {
          setSenderEmails(emails);
        }
      } catch (err) {
        if (isMounted) {
          setSenderError(err.message);
        }
      }
    }

    loadSenderEmails();

    return () => {
      isMounted = false;
    };
  }, [quotaAsOf]);

  const processCsvFile = async (file) => {
    setCsvFile(file);
    resetValidation();

    if (!file) {
      return;
    }

    try {
      const error = await validateCsvFile(file);
      if (error) {
        toast.error(error);
        return;
      }

      setIsValidating(true);
      const result = await verifyRecipientCsv(file);
      setValidationResult(result);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    void processCsvFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (isDisabled) {
      return;
    }

    const file = event.dataTransfer.files?.[0] || null;
    if (file) {
      void processCsvFile(file);
    }
  };

  const handleDispatch = async () => {
    setSenderError('');

    if (!validationResult?.matched?.length) {
      toast.error('Check your recipient list first. You need at least one matching vessel.');
      return;
    }

    if (scheduleMode === 'later') {
      if (!sendAt) {
        toast.error('Pick a date and time to schedule this email.');
        return;
      }

      const scheduledDate = new Date(sendAt);
      if (Number.isNaN(scheduledDate.getTime())) {
        toast.error('That date and time is not valid.');
        return;
      }
      if (scheduledDate.getTime() <= Date.now()) {
        toast.error('Pick a date and time in the future.');
        return;
      }
    }

    if (senderMode === 'manual' && selectedSenderIds.length === 0) {
      setSenderError('Select at least one sender email to continue.');
      return;
    }

    if (!activeAllocation.length) {
      const message =
        senderMode === 'manual'
          ? `Selected sender(s) only have ${selectedAvailableQuota.toLocaleString()} send(s) left for ${validationResult.matched.length} recipient(s).`
          : `No combination of senders has enough sends left (${quotaWindowLabel}) for ${validationResult.matched.length} recipient(s).`;
      setSenderError(message);
      return;
    }

    if (!subject.trim()) {
      toast.error('Subject is required.');
      return;
    }

    if (!body.trim()) {
      toast.error('Message is required.');
      return;
    }

    setIsDispatching(true);

    try {
      const payload = {
        subject: subject.trim(),
        text: body.trim(),
        recipients: validationResult.matched.map((recipient) => ({
          name: recipient.name,
          email: recipient.vesselEmail,
        })),
      };

      if (senderMode === 'manual') {
        payload.senderIds = selectedSenderIds;
      }

      if (scheduleMode === 'later' && sendAt) {
        payload.sendAt = new Date(sendAt).toISOString();
      }

      const result = await createDispatchJob(payload, attachments);

      showDispatchToast(result);
      resetForm();

      const refreshedEmails = await listEmails(quotaAsOf ? { asOf: quotaAsOf } : {});
      setSenderEmails(refreshedEmails);
    } catch (err) {
      toast.error(err.message || 'Failed to send email.');
    } finally {
      setIsDispatching(false);
    }
  };

  return (
    <div className="dispatch-wizard">
      <section className="dispatch-wizard__intro">
        <PageBreadcrumb current={emojify('✉️', 'Send Email')} />
        <h2>{emojify('🚀', 'Send Email')}</h2>
        <p>Send emails to vessels in a few steps.</p>
      </section>

      <section className="wizard-step">
        <header className="wizard-step__header">
          <span className="wizard-step__badge wizard-step__badge--active">1</span>
          <h3>{emojify('📥', 'Choose recipients')}</h3>
        </header>

        <label className="wizard-field__label" htmlFor="recipient-csv">
          {emojify('📥', 'Recipients')} (CSV file)
        </label>

        <div
          className={`wizard-upload${isDisabled ? ' wizard-upload--disabled' : ''}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={isDisabled ? undefined : handleDrop}
          onClick={isDisabled ? undefined : () => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (isDisabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
        >
          <input
            ref={fileInputRef}
            id="recipient-csv"
            type="file"
            accept=".csv,text/csv"
            className="wizard-upload__input"
            onChange={handleFileChange}
            disabled={isDisabled}
          />
          <span className="wizard-upload__icon" aria-hidden="true">
            <EmptyDocIcon />
          </span>
          <p className="wizard-upload__title">
            {isValidating
              ? 'Checking...'
              : csvFile
                ? csvFile.name
                : 'Drag and drop CSV here'}
          </p>
          <p className="wizard-upload__hint">
            {isValidating
              ? 'Matching vessels from your list'
              : 'Max 10MB • Needs name and port columns'}
          </p>
        </div>

        {validationResult && (
          <div className="wizard-validation" aria-live="polite">
            <div className="wizard-validation__card wizard-validation__card--success">
              <span className="wizard-validation__count">
                {validationResult.matched.length.toLocaleString()}
              </span>
              <span className="wizard-validation__label">Found</span>
            </div>
            <div className="wizard-validation__card wizard-validation__card--warning">
              <span className="wizard-validation__count">
                {validationResult.unmatched.length.toLocaleString()}
              </span>
              <span className="wizard-validation__label">Not found</span>
            </div>
            <div className="wizard-validation__card">
              <span className="wizard-validation__count">
                {validationResult.total.toLocaleString()}
              </span>
              <span className="wizard-validation__label">Total rows</span>
            </div>
          </div>
        )}
      </section>


      <section
        className={`wizard-step${!isRecipientsReady || isDisabled ? ' wizard-step--disabled' : ''}`}
      >
        <header className="wizard-step__header">
          <span
            className={`wizard-step__badge${isRecipientsReady ? ' wizard-step__badge--active' : ''}`}
          >
            2
          </span>
          <h3>{emojify('🚀', 'When to send')}</h3>
        </header>

        <p className="wizard-field__hint wizard-field__hint--spaced">
          Choose when to send this email. Sender capacity is calculated for this time.
        </p>

        <div className="wizard-sender-mode" role="radiogroup" aria-label="Send timing">
          <label
            className={`wizard-sender-mode__option${scheduleMode === 'now' ? ' wizard-sender-mode__option--selected' : ''}`}
          >
            <input
              type="radio"
              name="schedule-mode"
              value="now"
              checked={scheduleMode === 'now'}
              onChange={() => {
                setScheduleMode('now');
                setSendAt('');
              }}
              disabled={!isRecipientsReady || isDisabled}
            />
            <span className="wizard-sender-mode__content">
              <span className="wizard-sender-mode__title">{emojify('✉️', 'Send now')}</span>
              <span className="wizard-sender-mode__description">
                Send right away using current available quota.
              </span>
            </span>
          </label>

          <label
            className={`wizard-sender-mode__option${scheduleMode === 'later' ? ' wizard-sender-mode__option--selected' : ''}`}
          >
            <input
              type="radio"
              name="schedule-mode"
              value="later"
              checked={scheduleMode === 'later'}
              onChange={() => {
                setScheduleMode('later');
              }}
              disabled={!isRecipientsReady || isDisabled}
            />
            <span className="wizard-sender-mode__content">
              <span className="wizard-sender-mode__title">{emojify('🗓️', 'Schedule later')}</span>
              <span className="wizard-sender-mode__description">
                Pick a future date and time to check quota for that window.
              </span>
            </span>
          </label>
        </div>

        {scheduleMode === 'later' && (
          <div className="wizard-schedule-field">
            <label className="wizard-field__label" htmlFor="email-send-at">
              {emojify('🕒', 'Date & Time')}
            </label>
            <input
              id="email-send-at"
              type="datetime-local"
              className="wizard-input wizard-input--datetime"
              value={sendAt}
              onChange={(event) => setSendAt(event.target.value)}
              disabled={!isRecipientsReady || isDisabled}
            />
            {!isScheduleStepComplete && sendAt && (
              <p className="wizard-upload__error">
                {emojify('⚠️', 'Error')}: Pick a date and time in the future.
              </p>
            )}
          </div>
        )}
      </section>

      <section
        className={`wizard-step${!isScheduleStepComplete || isDisabled ? ' wizard-step--disabled' : ''}`}
      >
        <header className="wizard-step__header">
          <span
            className={`wizard-step__badge${isScheduleStepComplete ? ' wizard-step__badge--active' : ''}`}
          >
            3
          </span>
          <h3>{emojify('📧', 'Choose sender')}</h3>
        </header>

        <p className="wizard-field__hint wizard-field__hint--spaced">
          Choose which email account(s) will send these messages.
        </p>

        {isScheduleStepComplete && (
          <p className="wizard-quota-summary">
            Sends left:{' '}
            <strong>{totalAvailableQuota.toLocaleString()}</strong>
            <span className="wizard-quota-summary__meta">
              {' '}
              ({quotaWindowLabel})
            </span>
          </p>
        )}

        {scheduleMode === 'later' && isRecipientsReady && !isScheduleStepComplete && (
          <p className="wizard-field__hint wizard-field__hint--spaced">
            Pick a future date and time above to load available sender quota for that schedule.
          </p>
        )}

        <div className="wizard-sender-mode" role="radiogroup" aria-label="Sender selection mode">
          <label
            className={`wizard-sender-mode__option${senderMode === 'auto' ? ' wizard-sender-mode__option--selected' : ''}`}
          >
            <input
              type="radio"
              name="sender-mode"
              value="auto"
              checked={senderMode === 'auto'}
              onChange={() => {
                setSenderMode('auto');
                setSenderError('');
                setSelectedSenderIds([]);
              }}
              disabled={!isScheduleStepComplete || isDisabled}
            />
            <span className="wizard-sender-mode__content">
              <span className="wizard-sender-mode__title">Pick senders automatically</span>
              <span className="wizard-sender-mode__description">
                Splits the send across accounts with the most capacity left ({quotaWindowLabel}).
              </span>
              {senderMode === 'auto' && autoAllocation.length > 0 && (
                <span className="wizard-sender-mode__preview">
                  Plan: {formatAllocationSummary(autoAllocation)}
                </span>
              )}
              {senderMode === 'auto' && isScheduleStepComplete && autoAllocation.length === 0 && (
                <span className="wizard-sender-mode__preview wizard-sender-mode__preview--warning">
                  Combined senders only have {totalAvailableQuota.toLocaleString()} send(s) left for{' '}
                  {recipientCount.toLocaleString()} recipient(s).
                </span>
              )}
            </span>
          </label>

          <label
            className={`wizard-sender-mode__option${senderMode === 'manual' ? ' wizard-sender-mode__option--selected' : ''}`}
          >
            <input
              type="radio"
              name="sender-mode"
              value="manual"
              checked={senderMode === 'manual'}
              onChange={() => {
                setSenderMode('manual');
                setSenderError('');
              }}
              disabled={!isScheduleStepComplete || isDisabled}
            />
            <span className="wizard-sender-mode__content">
              <span className="wizard-sender-mode__title">Choose one or more senders</span>
              <span className="wizard-sender-mode__description">
                Select accounts below. Sends are split by remaining capacity (most available first).
              </span>
              {senderMode === 'manual' && selectedSenderIds.length > 0 && manualAllocation.length > 0 && (
                <span className="wizard-sender-mode__preview">
                  Plan: {formatAllocationSummary(manualAllocation)}
                </span>
              )}
              {senderMode === 'manual' &&
                selectedSenderIds.length > 0 &&
                manualAllocation.length === 0 && (
                  <span className="wizard-sender-mode__preview wizard-sender-mode__preview--warning">
                    Selected senders only have {selectedAvailableQuota.toLocaleString()} send(s) left
                    for {recipientCount.toLocaleString()} recipient(s).
                  </span>
                )}
            </span>
          </label>
        </div>

        {senderMode === 'manual' && (
          <>
            <label className="wizard-field__label" htmlFor="sender-search">
              {emojify('🔎', 'Search senders')}
            </label>
            <input
              id="sender-search"
              type="text"
              className="wizard-input"
              value={senderSearch}
              onChange={(event) => setSenderSearch(event.target.value)}
              placeholder="Search email, name, or provider..."
              disabled={!isScheduleStepComplete || isDisabled}
            />
            <div
              className={`wizard-sender-table data-page__table-wrap data-page__rdt${
                filteredSenderEmails.length > 10 ? ' wizard-sender-table--scrollable' : ''
              }`}
            >
              <DataTable
                columns={senderTableColumns}
                data={filteredSenderEmails}
                keyField="id"
                selectableRows
                selectableRowDisabled={(row) => !hasAnyQuota(row)}
                selectableRowSelected={isSenderRowSelected}
                onSelectedRowsChange={handleSenderRowsSelected}
                customStyles={senderTableStyles}
                pagination={false}
                noDataComponent={
                  <div className="data-page__empty">
                    {senderSearch.trim() && senderEmails.length
                      ? 'No matching sender emails.'
                      : 'No sender emails available.'}
                  </div>
                }
                dense
              />
            </div>
          </>
        )}

        {senderError && (
          <p className="wizard-upload__error">
            {emojify('⚠️', 'Error')}: {senderError}
          </p>
        )}
      </section>

      <section
        className={`wizard-step${!isMessageStepReady || isDisabled ? ' wizard-step--disabled' : ''}`}
      >
        <header className="wizard-step__header">
          <span
            className={`wizard-step__badge${isMessageStepReady ? ' wizard-step__badge--active' : ''}`}
          >
            4
          </span>
          <h3>{emojify('💬', 'Write your message')}</h3>
        </header>

        <label className="wizard-field__label" htmlFor="email-subject">
          {emojify('🏷️', 'Subject')}
        </label>
        <input
          id="email-subject"
          type="text"
          className="wizard-input"
          placeholder="e.g., Arrival notice: Port of Antwerp"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          disabled={!isMessageStepReady || isDisabled}
        />

        <label className="wizard-field__label" htmlFor="email-body">
          {emojify('💬', 'Message')}
        </label>
        <textarea
          id="email-body"
          className="wizard-textarea"
          placeholder="Write your email here..."
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={!isMessageStepReady || isDisabled}
        />

        <label className="wizard-field__label" htmlFor="email-attachments">
          {emojify('📎', 'Attachments')}
        </label>
        <p className="wizard-field__hint">
          Optional. Up to {MAX_ATTACHMENTS} files, {formatFileSize(MAX_ATTACHMENTS_TOTAL_SIZE)} total.
        </p>

        <div className="wizard-attachments">
          <input
            ref={attachmentInputRef}
            id="email-attachments"
            type="file"
            className="wizard-upload__input"
            multiple
            disabled={isDisabled || !isMessageStepReady}
            onChange={(event) => {
              const selected = Array.from(event.target.files || []);
              event.target.value = '';

              if (!selected.length) {
                return;
              }

              setAttachmentError('');

              setAttachments((current) => {
                const next = [...current];
                let totalSize = next.reduce((sum, file) => sum + file.size, 0);

                for (const file of selected) {
                  if (next.length >= MAX_ATTACHMENTS) {
                    setAttachmentError(`You can attach at most ${MAX_ATTACHMENTS} files.`);
                    break;
                  }

                  if (totalSize + file.size > MAX_ATTACHMENTS_TOTAL_SIZE) {
                    setAttachmentError(
                      `Total attachments must be ${formatFileSize(MAX_ATTACHMENTS_TOTAL_SIZE)} or less.`
                    );
                    break;
                  }

                  const duplicate = next.some(
                    (entry) =>
                      entry.name === file.name &&
                      entry.size === file.size &&
                      entry.lastModified === file.lastModified
                  );
                  if (duplicate) {
                    continue;
                  }

                  next.push(file);
                  totalSize += file.size;
                }

                return next;
              });
            }}
          />

          <button
            type="button"
            className="wizard-btn wizard-btn--secondary"
            disabled={isDisabled || !isMessageStepReady || attachments.length >= MAX_ATTACHMENTS}
            onClick={() => attachmentInputRef.current?.click()}
          >
            {!isEmojified && <AddIcon />}
            {emojify('📎', 'Attach File')}
          </button>

          {attachments.length > 0 && (
            <ul className="wizard-attachments__list">
              {attachments.map((file, index) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}-${index}`}>
                  <span className="wizard-attachments__name" title={file.name}>
                    {file.name}
                  </span>
                  <span className="wizard-attachments__meta">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="wizard-attachments__remove"
                    disabled={isDisabled}
                    onClick={() => {
                      setAttachments((current) => current.filter((_, i) => i !== index));
                      setAttachmentError('');
                    }}
                  >
                    {emojify('🗑️', 'Delete')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {attachmentError && (
          <p className="wizard-upload__error">
            {emojify('⚠️', 'Error')}: {attachmentError}
          </p>
        )}

        {!isMessageStepComplete && isMessageStepReady && (
          <p className="wizard-field__hint wizard-field__hint--spaced">
            Add a subject and message to continue to the summary.
          </p>
        )}
      </section>

      <section
        className={`wizard-step${!isSummaryStepReady || isDisabled ? ' wizard-step--disabled' : ''}`}
      >
        <header className="wizard-step__header">
          <span
            className={`wizard-step__badge${isSummaryStepReady ? ' wizard-step__badge--active' : ''}`}
          >
            5
          </span>
          <h3>{emojify('📋', 'Review & confirm')}</h3>
        </header>

        <p className="wizard-field__hint wizard-field__hint--spaced">
          Check everything below before you{' '}
          {scheduleMode === 'later' ? 'schedule' : 'send'}.
        </p>

        {isSummaryStepReady && (
          <dl className="wizard-summary">
            <div className="wizard-summary__row">
              <dt>{emojify('🚀', 'When')}</dt>
              <dd>
                {scheduleMode === 'later' && sendAt
                  ? formatDispatchDateTime(sendAt)
                  : 'Send now'}
              </dd>
            </div>

            <div className="wizard-summary__row">
              <dt>{emojify('📥', 'Recipients')}</dt>
              <dd>
                {recipientCount.toLocaleString()} matched
                {validationResult?.unmatched?.length
                  ? ` · ${validationResult.unmatched.length.toLocaleString()} not found`
                  : ''}
                {csvFile ? ` · ${csvFile.name}` : ''}
              </dd>
            </div>

            <div className="wizard-summary__row">
              <dt>{emojify('📧', 'Senders')}</dt>
              <dd>
                <span className="wizard-summary__line">
                  {senderMode === 'auto' ? 'Auto-select' : 'Manual selection'}
                  {' · '}
                  {quotaWindowLabel}
                </span>
                {activeAllocation.length > 0 && (
                  <span className="wizard-summary__line wizard-summary__line--accent">
                    {formatAllocationSummary(activeAllocation)}
                  </span>
                )}
              </dd>
            </div>

            <div className="wizard-summary__row">
              <dt>{emojify('🏷️', 'Subject')}</dt>
              <dd>{subject.trim()}</dd>
            </div>

            <div className="wizard-summary__row">
              <dt>{emojify('💬', 'Message')}</dt>
              <dd>
                <pre className="wizard-summary__body">{body.trim()}</pre>
              </dd>
            </div>

            <div className="wizard-summary__row">
              <dt>{emojify('📎', 'Attachments')}</dt>
              <dd>
                {attachments.length === 0
                  ? 'None'
                  : attachments
                      .map((file) => `${file.name} (${formatFileSize(file.size)})`)
                      .join(' · ')}
              </dd>
            </div>
          </dl>
        )}

        <div className="wizard-dispatch-actions">
          <button
            type="button"
            className="wizard-btn wizard-btn--secondary wizard-btn--dispatch"
            onClick={handleDispatch}
            disabled={isDisabled || !isSummaryStepReady}
          >
            {isDispatching ? (
              <span className="wizard-btn__spinner" aria-hidden="true" />
            ) : (
              !isEmojified && <SendIcon />
            )}
            {isDispatching
              ? emojify('🚀', 'Sending...')
              : scheduleMode === 'later'
                ? emojify('🗓️', 'Schedule Email')
                : emojify('✉️', 'Send Email')}
          </button>
        </div>
      </section>
    </div>
  );
}

export default SendEmail;
